import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { EntShell, Btn, Field, inputCls, Kicker, errText, useEntMe } from "@/components/enterprise/Ent";

const splitList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

const Section = ({ n, title, children }) => (
  <fieldset className="bg-white border border-[#ddd5c9] rounded-lg p-6 space-y-5">
    <legend className="px-2 -ml-2 font-display text-xl"><span className="text-[#7a1010] mr-2">{n}.</span>{title}</legend>
    {children}
  </fieldset>
);

const Choice = ({ name, value, checked, onChange, title, sub, testid }) => (
  <label className={`flex gap-3 p-4 border rounded-md cursor-pointer transition-colors ${checked ? "border-[#7a1010] bg-[#fbf6f4]" : "border-[#ddd5c9] hover:border-[#8a8178]"}`} data-testid={testid}>
    <input type="radio" name={name} value={value} checked={checked} onChange={onChange} className="mt-1 accent-[#7a1010]" />
    <span><span className="block font-semibold">{title}</span>{sub && <span className="block text-sm text-[#5a524b]">{sub}</span>}</span>
  </label>
);

const OrgFields = ({ org, setOrg }) => (
  <div className="grid sm:grid-cols-2 gap-4">
    <Field id="org-name" label="Organization name">
      <input id="org-name" className={inputCls} value={org.name} onChange={(e) => setOrg({ ...org, name: e.target.value })} required data-testid="setup-org-name" />
    </Field>
    <Field id="org-dept" label="Department or team (optional)">
      <input id="org-dept" className={inputCls} value={org.department} onChange={(e) => setOrg({ ...org, department: e.target.value })} data-testid="setup-org-dept" />
    </Field>
    <Field id="org-industry" label="Industry or category (optional)">
      <input id="org-industry" className={inputCls} value={org.industry} onChange={(e) => setOrg({ ...org, industry: e.target.value })} placeholder="e.g. Public sector, Healthcare, Education" data-testid="setup-org-industry" />
    </Field>
    <Field id="org-color" label="Brand color (optional)" hint="Used as the accent color on participant screens and reports.">
      <div className="flex gap-2 items-center">
        <input id="org-color" type="color" value={org.brand_hex || "#7a1010"} onChange={(e) => setOrg({ ...org, brand_hex: e.target.value })} className="h-11 w-14 rounded border border-[#bdb3a6]" data-testid="setup-org-color" />
        <span className="text-sm text-[#5a524b]">{org.brand_hex || "Default"}</span>
      </div>
    </Field>
  </div>
);

const Preview = ({ program, cz, orgName }) => {
  if (!program) return null;
  const teams = splitList(cz.team_names);
  const chars = splitList(cz.character_names);
  return (
    <aside className="bg-[#16130f] text-[#f1ece4] rounded-lg p-6" aria-label="Customization preview" data-testid="setup-preview">
      <div className="text-xs font-semibold tracking-[0.18em] uppercase text-[#e9c98a]">Preview</div>
      <div className="font-display text-2xl mt-2 text-white">{program.name}</div>
      <div className="text-sm text-[#d9d2c8]">Prepared for {orgName || "your organization"}</div>
      <div className="mt-5 text-sm font-semibold text-white">Teams</div>
      <div className="text-sm text-[#d9d2c8]">{teams.length ? teams.join(" · ") : "Team 1 · Team 2 · …"}</div>
      <div className="mt-4 text-sm font-semibold text-white">Roles</div>
      <ul className="text-sm text-[#d9d2c8] space-y-1 mt-1">
        {program.role_titles.map((t, i) => <li key={t}>{t}{chars[i] ? ` (${chars[i]})` : ""}</li>)}
      </ul>
      <div className="mt-4 text-sm"><span className="font-semibold text-white">Difficulty:</span> {cz.difficulty === "challenging" ? "Challenging (no hint)" : "Standard (hint included)"}</div>
    </aside>
  );
};

export default function EnterpriseSetup() {
  const { me } = useEntMe();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [cat, setCat] = useState(null);
  const [orgId, setOrgId] = useState("new");
  const [org, setOrg] = useState({ name: "", department: "", industry: "", brand_hex: "" });
  const [f, setF] = useState({
    program_id: params.get("program") || "", capacity_id: "team", option: params.get("option") === "customized" ? "customized" : "ready",
    requested_date: "", scheduling_help: false, assessments_enabled: true,
  });
  const [cz, setCz] = useState({ team_names: "", character_names: "", difficulty: "standard", language: "en", objectives: [], theme_notes: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Book a program | WasntMe.Party Enterprise";
    api.get("/enterprise/catalog").then((r) => setCat(r.data));
    if (params.get("canceled")) toast.message("Checkout canceled. Your booking wasn't charged.");
  }, [params]);
  useEffect(() => { if (me?.orgs?.length) setOrgId(me.orgs[0].org_id); }, [me]);

  const available = useMemo(() => (cat?.programs || []).filter((p) => p.status === "available"), [cat]);
  const program = available.find((p) => p.id === f.program_id);
  const orgName = orgId === "new" ? org.name : me?.orgs?.find((o) => o.org_id === orgId)?.name;

  const submit = async (e) => {
    e.preventDefault();
    if (!f.program_id) return toast.error("Choose a program.");
    if (orgId === "new" && !org.name.trim()) return toast.error("Enter your organization name.");
    setBusy(true);
    try {
      let oid = orgId;
      if (orgId === "new") {
        const r = await api.post("/enterprise/orgs", org, { withCredentials: true });
        oid = r.data.org.org_id;
        setOrgId(oid);
      }
      const body = {
        ...f, org_id: oid, origin_url: window.location.origin,
        customization: { ...cz, team_names: splitList(cz.team_names), character_names: splitList(cz.character_names) },
      };
      const r = await api.post("/enterprise/events", body, { withCredentials: true });
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
        return;
      }
      toast.success("Customization submitted. We'll review it and email you to confirm.");
      navigate(`/enterprise/events/${r.data.event_id}`);
    } catch (e2) {
      toast.error(errText(e2));
    } finally {
      setBusy(false);
    }
  };

  if (!me || !cat) return <EntShell><div className="max-w-4xl mx-auto px-6 py-20" aria-busy="true">Loading…</div></EntShell>;

  const cap = cat.capacities.find((c) => c.id === f.capacity_id);
  return (
    <EntShell>
      <form onSubmit={submit} className="max-w-6xl mx-auto px-6 py-14" data-testid="setup-form">
        <Kicker>Team Impact™ booking</Kicker>
        <h1 className="font-display text-4xl sm:text-5xl mt-3">Set up your event.</h1>
        <p className="mt-3 text-[15px] text-[#3b3530] max-w-2xl">Please don't include classified, confidential, or sensitive employee information anywhere in this form.</p>

        <div className="mt-10 grid lg:grid-cols-[1fr_340px] gap-8 items-start">
          <div className="space-y-6">
            <Section n={1} title="Organization">
              {me.orgs.length > 0 && (
                <Field id="org-select" label="Organization">
                  <select id="org-select" className={inputCls} value={orgId} onChange={(e) => setOrgId(e.target.value)} data-testid="setup-org-select">
                    {me.orgs.map((o) => <option key={o.org_id} value={o.org_id}>{o.name}{o.department ? ` · ${o.department}` : ""}</option>)}
                    <option value="new">+ Add another organization or department</option>
                  </select>
                </Field>
              )}
              {orgId === "new" && <OrgFields org={org} setOrg={setOrg} />}
            </Section>

            <Section n={2} title="Program">
              <div className="grid sm:grid-cols-3 gap-3" role="radiogroup" aria-label="Program">
                {available.map((p) => (
                  <Choice key={p.id} name="program" value={p.id} checked={f.program_id === p.id} onChange={() => setF({ ...f, program_id: p.id })}
                    title={p.name} sub={p.objectives.map((o) => o.label).join(", ")} testid={`setup-program-${p.id}`} />
                ))}
              </div>
            </Section>

            <Section n={3} title="Purchasing option and capacity">
              <div className="grid sm:grid-cols-2 gap-3">
                <Choice name="option" value="ready" checked={f.option === "ready"} onChange={() => setF({ ...f, option: "ready" })}
                  title="Ready-to-Play" sub="Standard program. Pay by card and get confirmed instantly." testid="setup-option-ready" />
                <Choice name="option" value="customized" checked={f.option === "customized"} onChange={() => setF({ ...f, option: "customized" })}
                  title="Customized Experience" sub="Personalized to your team. Quoted after review." testid="setup-option-customized" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {cat.capacities.map((c) => (
                  <Choice key={c.id} name="capacity" value={c.id} checked={f.capacity_id === c.id} onChange={() => setF({ ...f, capacity_id: c.id })}
                    title={`${c.label}: ${c.people}`} sub={c.price_label} testid={`setup-capacity-${c.id}`} />
                ))}
              </div>
            </Section>

            {f.option === "customized" && (
              <Section n={4} title="Customization (TEAMVERSE™)">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field id="cz-teams" label="Team names (optional)" hint="Separate with commas. Teams of up to 6 are formed in join order.">
                    <input id="cz-teams" className={inputCls} value={cz.team_names} onChange={(e) => setCz({ ...cz, team_names: e.target.value })} placeholder="Falcons, Owls, Ravens" data-testid="setup-cz-teams" />
                  </Field>
                  <Field id="cz-chars" label="Fictional character names (optional)" hint="Up to 6, one for each role, in order.">
                    <input id="cz-chars" className={inputCls} value={cz.character_names} onChange={(e) => setCz({ ...cz, character_names: e.target.value })} placeholder="Avery, Jordan, Riley" data-testid="setup-cz-chars" />
                  </Field>
                  <Field id="cz-diff" label="Difficulty">
                    <select id="cz-diff" className={inputCls} value={cz.difficulty} onChange={(e) => setCz({ ...cz, difficulty: e.target.value })} data-testid="setup-cz-difficulty">
                      <option value="standard">Standard (hint included)</option>
                      <option value="challenging">Challenging (no hint)</option>
                    </select>
                  </Field>
                  <Field id="cz-lang" label="Preferred language">
                    <select id="cz-lang" className={inputCls} value={cz.language} onChange={(e) => setCz({ ...cz, language: e.target.value })} data-testid="setup-cz-language">
                      {cat.languages.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                    </select>
                  </Field>
                </div>
                <fieldset>
                  <legend className="text-sm font-semibold">Additional learning objectives (optional)</legend>
                  <div className="mt-2 grid sm:grid-cols-3 gap-2">
                    {cat.objectives.filter((o) => !program?.objectives.some((p) => p.id === o.id)).map((o) => (
                      <label key={o.id} className="flex items-center gap-2 text-sm">
                        <input type="checkbox" className="accent-[#7a1010]" checked={cz.objectives.includes(o.id)} data-testid={`setup-cz-obj-${o.id}`}
                          onChange={(e) => setCz({ ...cz, objectives: e.target.checked ? [...cz.objectives, o.id] : cz.objectives.filter((x) => x !== o.id) })} />
                        {o.label}
                      </label>
                    ))}
                  </div>
                </fieldset>
                <Field id="cz-notes" label="Theme or mission notes (optional)" hint="Tell us about your mission, values, or goals. Public information only. Our team will adapt the template.">
                  <textarea id="cz-notes" rows={3} maxLength={1500} className={inputCls} value={cz.theme_notes} onChange={(e) => setCz({ ...cz, theme_notes: e.target.value })} data-testid="setup-cz-notes" />
                </Field>
              </Section>
            )}

            <Section n={f.option === "customized" ? 5 : 4} title="Scheduling and reflections">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field id="ev-date" label="Requested event date (optional)">
                  <input id="ev-date" type="date" className={inputCls} value={f.requested_date} onChange={(e) => setF({ ...f, requested_date: e.target.value })} data-testid="setup-date" />
                </Field>
                <label className="flex items-center gap-2 text-sm sm:pt-8">
                  <input type="checkbox" className="accent-[#7a1010]" checked={f.scheduling_help} onChange={(e) => setF({ ...f, scheduling_help: e.target.checked })} data-testid="setup-scheduling-help" />
                  I'd like help with scheduling
                </label>
              </div>
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className="accent-[#7a1010] mt-1" checked={f.assessments_enabled} onChange={(e) => setF({ ...f, assessments_enabled: e.target.checked })} data-testid="setup-assessments" />
                <span><b>Turn on the optional pre- and post-event reflections.</b> These are short, anonymous self-ratings that let your report compare before and after.</span>
              </label>
            </Section>
          </div>

          <div className="space-y-5 lg:sticky lg:top-24">
            {f.option === "customized" && <Preview program={program} cz={cz} orgName={orgName} />}
            <div className="bg-white border border-[#ddd5c9] rounded-lg p-6" data-testid="setup-summary">
              <div className="text-xs font-semibold tracking-[0.18em] uppercase text-[#5a524b]">Summary</div>
              <div className="mt-3 font-display text-xl">{program?.name || "Choose a program"}</div>
              <div className="text-sm text-[#5a524b]">{cap?.label}: {cap?.people}</div>
              <div className="mt-4 text-2xl font-semibold" data-testid="setup-total">{f.option === "ready" ? cap?.price_label : "Quoted after review"}</div>
              <Btn type="submit" className="w-full mt-5" disabled={busy || !f.program_id} data-testid="setup-submit">
                {busy ? "Please wait…" : f.option === "ready" ? "Continue to secure payment" : "Submit for review"}
              </Btn>
              <p className="mt-3 text-xs text-[#5a524b]">Need several departments or recurring events? <a href="/enterprise/proposal" className="underline">Request a proposal</a>.</p>
            </div>
          </div>
        </div>
      </form>
    </EntShell>
  );
}
